// src/ast.ts
import { IfOpSchema, IfTransformFnSchema } from "@rock/types";
var HQL_SINCE_FNS = IfTransformFnSchema.options;
var HQL_OPS = IfOpSchema.options;

// src/grammar.ts
var HQL_KEYWORDS = /* @__PURE__ */ new Set(["and", "or", "not", "in", "is", "unknown", "true", "false"]);
var HQL_WORD_CHAR = /[A-Za-z0-9_:.$~-]/;
var HQL_NUMBER = /^-?\d+(\.\d+)?$/;
function isKeyword(word) {
  return HQL_KEYWORDS.has(word.toLowerCase());
}

// src/lexer.ts
var PUNCT = /* @__PURE__ */ new Set(["(", ")", "[", "]", ","]);
var CLOSING_QUOTE = { '"': '"', "'": "'", "\u201C": "\u201D", "\u2018": "\u2019" };
function lex(text) {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      i++;
    } else if (PUNCT.has(ch)) {
      tokens.push({ type: "punct", char: ch, start: i, end: i + 1 });
      i++;
    } else if (ch in CLOSING_QUOTE) {
      const read = readString(text, i);
      if ("error" in read) return { ok: false, error: read.error };
      tokens.push(read.token);
      i = read.token.end;
    } else if ("=!<>".includes(ch)) {
      const read = readOp(text, i);
      if ("error" in read) return { ok: false, error: read.error };
      tokens.push(read.token);
      i = read.token.end;
    } else if (HQL_WORD_CHAR.test(ch)) {
      const token = readWord(text, i);
      tokens.push(token);
      i = token.end;
    } else {
      return { ok: false, error: { message: `Unexpected character "${ch}"`, start: i, end: i + 1 } };
    }
  }
  tokens.push({ type: "eof", start: text.length, end: text.length });
  return { ok: true, tokens };
}
function readWord(text, start) {
  let end = start;
  while (end < text.length && HQL_WORD_CHAR.test(text[end])) end++;
  const word = text.slice(start, end);
  return HQL_NUMBER.test(word) ? { type: "number", value: Number(word), raw: word, start, end } : { type: "word", text: word, start, end };
}
function readString(text, start) {
  const close = CLOSING_QUOTE[text[start]];
  let value = "";
  let i = start + 1;
  while (i < text.length) {
    const ch = text[i];
    if (ch === "\\" && i + 1 < text.length) {
      value += text[i + 1];
      i += 2;
    } else if (ch === close) {
      return { token: { type: "string", value, start, end: i + 1 } };
    } else {
      value += ch;
      i++;
    }
  }
  return { error: { message: "This quote is never closed", start, end: text.length } };
}
function readOp(text, start) {
  const two = text.slice(start, start + 2);
  const token = (op, length) => ({
    token: { type: "op", op, start, end: start + length }
  });
  if (two === "==" || two === "!=" || two === ">=" || two === "<=") return token(two, 2);
  const one = text[start];
  if (one === "=") return token("==", 1);
  if (one === ">" || one === "<") return token(one, 1);
  return { error: { message: 'Unexpected "!". Use != to compare, or NOT to negate', start, end: start + 1 } };
}

// src/parser.ts
var MAX_DEPTH = 64;
var RESERVED_SUBJECTS = /* @__PURE__ */ new Set(["home", "equipment"]);
function parseHql(text) {
  if (!text.trim()) {
    return {
      ok: false,
      errors: [{ message: "The query is empty. Write `home` to match every home.", start: 0, end: text.length }]
    };
  }
  const lexed = lex(text);
  if (!lexed.ok) return { ok: false, errors: [lexed.error] };
  try {
    const cursor = new TokenCursor(lexed.tokens);
    const ast = parseOr(cursor);
    const next = cursor.peek();
    if (next.type !== "eof") {
      throw syntaxError(`Expected AND or OR before ${describe(next)}`, next);
    }
    return { ok: true, ast };
  } catch (err) {
    if (err instanceof HqlSyntaxError) return { ok: false, errors: [err.error] };
    throw err;
  }
}
var HqlSyntaxError = class extends Error {
  error;
  constructor(error) {
    super(error.message);
    this.error = error;
  }
};
var TokenCursor = class {
  tokens;
  index = 0;
  depth = 0;
  constructor(tokens) {
    this.tokens = tokens;
  }
  peek() {
    return this.tokens[this.index];
  }
  next() {
    const token = this.tokens[this.index];
    if (token.type !== "eof") this.index++;
    return token;
  }
  /** Consume the keyword if it's next (case-insensitive). */
  acceptKeyword(keyword) {
    const token = this.peek();
    return isWord(token, keyword) ? this.next() : void 0;
  }
  acceptPunct(char) {
    const token = this.peek();
    return token.type === "punct" && token.char === char ? this.next() : void 0;
  }
  expectPunct(char, context) {
    const token = this.acceptPunct(char);
    if (!token) throw syntaxError(`Expected "${char}" ${context}, found ${describe(this.peek())}`, this.peek());
    return token;
  }
  /** Run `parse` one level deeper (a NOT or a group), refusing input that nests without end. */
  nested(at, parse) {
    if (++this.depth > MAX_DEPTH) throw syntaxError("The query nests too deeply", at);
    try {
      return parse();
    } finally {
      this.depth--;
    }
  }
};
function parseOr(cursor) {
  const operands = [parseAnd(cursor)];
  while (cursor.acceptKeyword("or")) operands.push(parseAnd(cursor));
  return combineQuery("or", operands);
}
function parseAnd(cursor) {
  const operands = [parseNot(cursor)];
  while (cursor.acceptKeyword("and")) operands.push(parseNot(cursor));
  return combineQuery("and", operands);
}
function parseNot(cursor) {
  const not2 = cursor.acceptKeyword("not");
  if (not2) {
    const operand = cursor.nested(not2, () => parseNot(cursor));
    return { kind: "not", operand, span: { start: not2.start, end: operand.span.end } };
  }
  const open = cursor.acceptPunct("(");
  if (open) {
    const inner = cursor.nested(open, () => parseOr(cursor));
    const close = cursor.expectPunct(")", "to close the group");
    return { ...inner, span: { start: open.start, end: close.end } };
  }
  return parseTerm(cursor);
}
function parseTerm(cursor) {
  const token = cursor.next();
  if (token.type !== "word" || isKeyword(token.text)) {
    throw syntaxError(`Expected an equipment type, "home" or "equipment", found ${describe(token)}`, token);
  }
  const subject = RESERVED_SUBJECTS.has(token.text.toLowerCase()) ? token.text.toLowerCase() : token.text;
  const subjectSpan = spanOf(token);
  if (!cursor.acceptPunct("[")) {
    return { kind: "term", subject, span: subjectSpan, subjectSpan };
  }
  const filter = parseFilterOr(cursor);
  const close = cursor.expectPunct("]", "to close the filter");
  return { kind: "term", subject, filter, span: { start: token.start, end: close.end }, subjectSpan };
}
function parseFilterOr(cursor) {
  const operands = [parseFilterAnd(cursor)];
  while (cursor.acceptKeyword("or")) operands.push(parseFilterAnd(cursor));
  return combineFilter("or", operands);
}
function parseFilterAnd(cursor) {
  const operands = [parseFilterNot(cursor)];
  while (cursor.acceptKeyword("and")) operands.push(parseFilterNot(cursor));
  return combineFilter("and", operands);
}
function parseFilterNot(cursor) {
  const not2 = cursor.acceptKeyword("not");
  if (not2) {
    const operand = cursor.nested(not2, () => parseFilterNot(cursor));
    return { kind: "not", operand, span: { start: not2.start, end: operand.span.end } };
  }
  const open = cursor.acceptPunct("(");
  if (open) {
    const inner = cursor.nested(open, () => parseFilterOr(cursor));
    const close = cursor.expectPunct(")", "to close the group");
    return { ...inner, span: { start: open.start, end: close.end } };
  }
  return parseComparison(cursor);
}
function parseComparison(cursor) {
  const operand = parseOperand(cursor);
  const token = cursor.next();
  if (token.type === "op") {
    const value = parseLiteral(cursor);
    return { kind: "compare", operand, op: token.op, value, span: join(operand.span, value.span) };
  }
  if (isWord(token, "in")) {
    cursor.expectPunct("(", "after IN");
    const values = [parseLiteral(cursor)];
    while (cursor.acceptPunct(",")) values.push(parseLiteral(cursor));
    const close = cursor.expectPunct(")", "to close the IN list");
    return { kind: "in", operand, values, span: { start: operand.span.start, end: close.end } };
  }
  if (isWord(token, "is")) {
    const unknown = cursor.acceptKeyword("unknown");
    if (!unknown) throw syntaxError(`Expected "unknown" after IS, found ${describe(cursor.peek())}`, cursor.peek());
    return { kind: "is-unknown", operand, span: { start: operand.span.start, end: unknown.end } };
  }
  throw syntaxError(
    `Expected a comparison after "${operand.field}" (==, !=, <, <=, >, >=, IN or IS UNKNOWN), found ${describe(token)}`,
    token
  );
}
function parseOperand(cursor) {
  const token = cursor.next();
  if (token.type !== "word" || isKeyword(token.text)) {
    throw syntaxError(`Expected a field name, found ${describe(token)}`, token);
  }
  if (!cursor.acceptPunct("(")) return { field: token.text, span: spanOf(token), fieldSpan: spanOf(token) };
  const fn = token.text.toLowerCase();
  if (!isSinceFn(fn)) {
    throw syntaxError(`Unknown function "${token.text}". Use ${HQL_SINCE_FNS.join(", ")}`, token);
  }
  const field = cursor.next();
  if (field.type !== "word" || isKeyword(field.text)) {
    throw syntaxError(`Expected a field name inside ${fn}(), found ${describe(field)}`, field);
  }
  const close = cursor.expectPunct(")", `to close ${fn}(`);
  return { field: field.text, fn, span: { start: token.start, end: close.end }, fieldSpan: spanOf(field) };
}
function parseLiteral(cursor) {
  const token = cursor.next();
  const span = spanOf(token);
  if (token.type === "number") return { kind: "number", value: token.value, raw: token.raw, span };
  if (token.type === "string") return { kind: "string", value: token.value, span };
  if (token.type === "word") {
    const lower = token.text.toLowerCase();
    if (lower === "true" || lower === "false") return { kind: "boolean", value: lower === "true", span };
    if (lower === "unknown") throw syntaxError("To test for a missing value, write IS UNKNOWN", token);
    if (!isKeyword(lower)) return { kind: "string", value: token.text, span };
  }
  throw syntaxError(`Expected a value, found ${describe(token)}`, token);
}
function combineQuery(kind, operands) {
  if (operands.length === 1) return operands[0];
  const flat = operands.flatMap(
    (operand) => (operand.kind === "and" || operand.kind === "or") && operand.kind === kind ? operand.operands : [operand]
  );
  const span = join(operands[0].span, operands[operands.length - 1].span);
  return kind === "and" ? { kind: "and", operands: flat, span } : { kind: "or", operands: flat, span };
}
function combineFilter(kind, operands) {
  if (operands.length === 1) return operands[0];
  const flat = operands.flatMap(
    (operand) => (operand.kind === "and" || operand.kind === "or") && operand.kind === kind ? operand.operands : [operand]
  );
  const span = join(operands[0].span, operands[operands.length - 1].span);
  return kind === "and" ? { kind: "and", operands: flat, span } : { kind: "or", operands: flat, span };
}
function isWord(token, word) {
  return token.type === "word" && token.text.toLowerCase() === word;
}
function isSinceFn(value) {
  return HQL_SINCE_FNS.includes(value);
}
function spanOf(token) {
  return { start: token.start, end: token.end };
}
function join(a, b) {
  return { start: a.start, end: b.end };
}
function describe(token) {
  switch (token.type) {
    case "eof":
      return "the end of the query";
    case "word":
      return `"${token.text}"`;
    case "number":
      return token.raw;
    case "string":
      return `"${token.value}"`;
    case "op":
      return `"${token.op}"`;
    case "punct":
      return `"${token.char}"`;
  }
}
function syntaxError(message, token) {
  return new HqlSyntaxError({ message, start: token.start, end: token.end });
}

// src/print.ts
function printHql(ast) {
  return printExpr(ast, "or");
}
var RANK = { or: 0, and: 1, not: 2 };
function printExpr(node, parent) {
  switch (node.kind) {
    case "term":
      return node.filter ? `${node.subject}[${printFilter(node.filter, "or")}]` : node.subject;
    case "not":
      return `NOT ${printExpr(node.operand, "not")}`;
    case "and":
    case "or": {
      const text = node.operands.map((operand) => printExpr(operand, node.kind)).join(` ${node.kind.toUpperCase()} `);
      return RANK[node.kind] < RANK[parent] ? `(${text})` : text;
    }
  }
}
function printFilter(node, parent) {
  switch (node.kind) {
    case "not":
      return `NOT ${printFilter(node.operand, "not")}`;
    case "and":
    case "or": {
      const text = node.operands.map((operand) => printFilter(operand, node.kind)).join(` ${node.kind.toUpperCase()} `);
      return RANK[node.kind] < RANK[parent] ? `(${text})` : text;
    }
    default:
      return printComparison(node);
  }
}
function printComparison(node) {
  const operand = printOperand(node.operand);
  switch (node.kind) {
    case "compare":
      return `${operand} ${node.op} ${printLiteral(node.value)}`;
    case "in":
      return `${operand} IN (${node.values.map(printLiteral).join(", ")})`;
    case "is-unknown":
      return `${operand} IS UNKNOWN`;
  }
}
function printOperand(operand) {
  return operand.fn ? `${operand.fn}(${operand.field})` : operand.field;
}
var BARE_SLUG = new RegExp(`^[A-Za-z_]${HQL_WORD_CHAR.source}*$`);
function printLiteral(literal) {
  switch (literal.kind) {
    case "number":
      return literal.raw;
    case "boolean":
      return literal.value ? "true" : "false";
    case "string":
      return BARE_SLUG.test(literal.value) && !isKeyword(literal.value) ? literal.value : `"${literal.value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }
}

// src/builders.ts
var NO_SPAN = { start: 0, end: 0 };
function equipmentIdQuery(id) {
  return printHql({
    kind: "term",
    subject: "equipment",
    filter: {
      kind: "compare",
      operand: { field: "id", span: NO_SPAN, fieldSpan: NO_SPAN },
      op: "==",
      value: { kind: "string", value: id, span: NO_SPAN },
      span: NO_SPAN
    },
    span: NO_SPAN,
    subjectSpan: NO_SPAN
  });
}
function canonicalHql(text) {
  const parsed = parseHql(text);
  return parsed.ok ? printHql(parsed.ast) : text.trim();
}

// src/catalog.ts
function equipmentTypesById(catalog) {
  return new Map([...catalog.equipmentTypes, ...catalog.customEquipmentTypes ?? []].map((type) => [type.id, type]));
}

// src/brands.ts
import { routeSharedNameplate, sharedNameplateForKey } from "@rock/brands";
import { isMeaningfulBrandName, normalizeBrandName } from "@rock/util";
function matchBrandName(brands, name, sharedNameplates) {
  if (!isMeaningfulBrandName(name)) return { kind: "none" };
  const index = brandIndex(brands);
  const key = normalizeBrandName(name).key;
  const shared = sharedNameplateForKey(key, sharedNameplates);
  if (shared) return { kind: "shared", qualified: qualifiedNames(brands, shared.sides) };
  for (const tier of [index.identity, index.display]) {
    const entries = tier.get(key) ?? [];
    if (entries.length === 1) return { kind: "catalog", id: entries[0].id, name: entries[0].name };
    if (entries.length > 1) return { kind: "duplicate", names: entries.map((entry) => entry.canonicalName) };
  }
  return { kind: "uncatalogued", key };
}
function resolveUnitBrand(brands, name, spaceId, sharedNameplates) {
  if (!name) return {};
  const match = matchBrandName(brands, name, sharedNameplates);
  switch (match.kind) {
    case "catalog":
      return { brandId: match.id };
    case "uncatalogued":
      return { brandKey: match.key };
    case "shared": {
      const routed = routeSharedNameplate(name, spaceId, sharedNameplates);
      return routed && brands.some((entry) => entry.id === routed) ? { brandId: routed } : {};
    }
    case "duplicate":
    case "none":
      return {};
  }
}
var INDEX_CACHE = /* @__PURE__ */ new WeakMap();
function brandIndex(brands) {
  const cached = INDEX_CACHE.get(brands);
  if (cached) return cached;
  const index = { identity: /* @__PURE__ */ new Map(), display: /* @__PURE__ */ new Map() };
  for (const entry of brands) {
    const names = [entry.canonicalName, ...entry.aliases ?? []];
    const lines = entry.lines ?? [];
    addKeys(index.identity, entry, [
      ...names,
      ...lines.flatMap((line) => names.map((name) => `${name} ${line.displayName}`))
    ]);
    addKeys(index.display, entry, [entry.name, ...lines.map((line) => `${entry.name} ${line.displayName}`)]);
  }
  INDEX_CACHE.set(brands, index);
  return index;
}
function addKeys(map, entry, names) {
  const keys = new Set(names.map((name) => normalizeBrandName(name).key).filter(Boolean));
  for (const key of keys) {
    const entries = map.get(key) ?? [];
    if (!entries.some((existing) => existing.id === entry.id)) entries.push(entry);
    map.set(key, entries);
  }
}
function qualifiedNames(brands, sides) {
  return brands.filter((entry) => sides.includes(entry.id)).map((entry) => entry.canonicalName).sort();
}

// src/fields.ts
import { isSelectField } from "@rock/types";
function fieldKindOf(field) {
  switch (field.dataType) {
    case "boolean":
      return "boolean";
    case "select":
      return isSelectField(field) ? "select" : "text";
    case "number":
    case "quantity":
      return "number";
    case "year":
      return "year";
    case "month-year":
    case "date":
      return "date";
    case "text":
    case "model-number":
    case "serial-number":
    case "brand":
      return "text";
    case "dimensions":
    case "address":
    case "image":
    case "room-id":
      return "opaque";
  }
}
var HQL_CORE_FIELDS = [
  { key: "id", label: "Equipment id", kind: "id", dataType: "id" },
  { key: "brand", label: "Brand", kind: "brand", dataType: "brand" },
  { key: "modelNumber", label: "Model number", kind: "text", dataType: "model-number" },
  { key: "serialNumber", label: "Serial number", kind: "text", dataType: "serial-number" }
];
var EQUALITY = ["==", "!="];
var ORDERED = ["==", "!=", ">", ">=", "<", "<="];
function opsFor(kind) {
  switch (kind) {
    case "boolean":
      return { ops: EQUALITY, allowsIn: false };
    case "select":
    case "text":
    case "brand":
    case "id":
      return { ops: EQUALITY, allowsIn: true };
    case "number":
    case "year":
      return { ops: ORDERED, allowsIn: true };
    case "date":
    case "opaque":
      return { ops: [], allowsIn: false };
  }
}
var SINCE_OPS = { ops: ORDERED, allowsIn: true };
function acceptsSinceFn(kind) {
  return kind === "year" || kind === "date";
}
var SINCE_UNITS = {
  "years-since": "years",
  "months-since": "months",
  "days-since": "days"
};
function describeKind(kind) {
  switch (kind) {
    case "boolean":
      return "a yes/no field";
    case "select":
      return "a select field";
    case "number":
      return "a number field";
    case "year":
      return "a year field";
    case "date":
      return "a date field";
    case "text":
      return "a text field";
    case "brand":
      return "a brand field";
    case "id":
      return "an id";
    case "opaque":
      return "a field that only supports IS UNKNOWN";
  }
}

// src/compile.ts
function checkHql(text, catalog, options = {}) {
  const parsed = parseHql(text);
  if (!parsed.ok) return { errors: parsed.errors, warnings: [] };
  return { ...compileHql(parsed.ast, catalog, options), ast: parsed.ast };
}
function compileHql(ast, catalog, options = {}) {
  const compiler = new Compiler(catalog, options, ast);
  const root = compiler.node(ast, false);
  if (compiler.errors.length > 0) return { errors: compiler.errors, warnings: compiler.warnings };
  return {
    compiled: {
      text: printHql(ast),
      root,
      requiredSpaceIds: [...compiler.spaceIds].sort(),
      equipmentTypeIds: [...compiler.equipmentTypeIds].sort(),
      infoTypeIds: [...compiler.infoTypeIds].sort(),
      readsAllEquipment: compiler.readsAllEquipment,
      coversEquipment: compiler.coversEquipment
    },
    errors: [],
    warnings: compiler.warnings
  };
}
function sameHql(a, b, catalog, options = {}) {
  const left = checkHql(a, catalog, options).compiled;
  const right = checkHql(b, catalog, options).compiled;
  if (left && right) return JSON.stringify(left.root) === JSON.stringify(right.root);
  return canonicalHql(a) === canonicalHql(b);
}
var INVALID_NODE = { kind: "not", operand: { kind: "home" } };
var INVALID_TEST = {
  kind: "test",
  read: { source: "unit", key: "", kind: "opaque" },
  check: { kind: "unknown" }
};
var Compiler = class {
  errors = [];
  warnings = [];
  spaceIds = /* @__PURE__ */ new Set();
  equipmentTypeIds = /* @__PURE__ */ new Set();
  infoTypeIds = /* @__PURE__ */ new Set();
  readsAllEquipment = false;
  coversEquipment = false;
  catalog;
  equipmentTypes;
  infoTypes;
  knownSpaceIds;
  constructor(catalog, { spaceId }, ast) {
    this.catalog = catalog;
    this.equipmentTypes = equipmentTypesById(catalog);
    this.infoTypes = new Map(catalog.infoTypes.map((type) => [type.id, type]));
    this.knownSpaceIds = new Set(catalog.spaceIds);
    if (spaceId) {
      this.spaceIds.add(spaceId);
      if (!this.knownSpaceIds.has(spaceId)) {
        this.warnings.push({
          message: `This consideration belongs to the "${spaceId}" space, which isn't published, so no home can complete it and the query waits`,
          ...ast.span
        });
      }
    }
  }
  /** `underNot`: inside some NOT, where a term can decide whether the query applies but covers nothing. */
  node(ast, underNot) {
    switch (ast.kind) {
      case "and":
      case "or":
        if (ast.operands.length === 0) {
          return this.invalid(`An empty ${ast.kind.toUpperCase()} can't be evaluated`, ast.span);
        }
        return { kind: ast.kind, operands: ast.operands.map((operand) => this.node(operand, underNot)) };
      case "not":
        return { kind: "not", operand: this.node(ast.operand, true) };
      case "term":
        return this.term(ast, underNot);
    }
  }
  term(term, underNot) {
    if (term.subject === "home") {
      return { kind: "home", filter: this.filterOf(term, (operand) => this.infoField(operand)) };
    }
    if (!underNot) this.coversEquipment = true;
    if (term.subject === "equipment") {
      this.readsAllEquipment = true;
      return { kind: "units", filter: this.filterOf(term, (operand) => this.unitField(void 0, operand)) };
    }
    const type = this.equipmentTypes.get(term.subject);
    if (!type) {
      const hint = didYouMean(term.subject, [...this.equipmentTypes.keys()]);
      return this.invalid(`Unknown equipment type "${term.subject}"${hint}`, term.subjectSpan);
    }
    if (!this.knownSpaceIds.has(type.spaceId)) {
      return this.invalid(
        `"${type.id}" belongs to the "${type.spaceId}" space, which isn't published (or no longer exists)`,
        term.subjectSpan
      );
    }
    this.equipmentTypeIds.add(type.id);
    this.spaceIds.add(type.spaceId);
    return {
      kind: "units",
      typeId: type.id,
      filter: this.filterOf(term, (operand) => this.unitField(type, operand))
    };
  }
  filterOf(term, resolve) {
    return term.filter ? this.filter(term.filter, resolve) : void 0;
  }
  filter(node, resolve) {
    switch (node.kind) {
      case "and":
      case "or":
        if (node.operands.length === 0) {
          this.error(`An empty ${node.kind.toUpperCase()} can't be evaluated`, node.span);
          return INVALID_TEST;
        }
        return { kind: node.kind, operands: node.operands.map((operand) => this.filter(operand, resolve)) };
      case "not":
        return { kind: "not", operand: this.filter(node.operand, resolve) };
      default:
        return this.comparison(node, resolve);
    }
  }
  comparison(node, resolve) {
    const field = resolve(node.operand);
    if (!field) return INVALID_TEST;
    const fn = node.operand.fn;
    if (fn && !acceptsSinceFn(field.kind)) {
      this.error(
        `${fn}() counts from a year or a date, but "${field.key}" is ${describeKind(field.kind)}`,
        node.operand.span
      );
      return INVALID_TEST;
    }
    const check2 = this.check(node, field, fn);
    if (!check2) return INVALID_TEST;
    return { kind: "test", read: { source: field.source, key: field.key, kind: field.kind, fn }, check: check2 };
  }
  check(node, field, fn) {
    if (node.kind === "is-unknown") return { kind: "unknown" };
    const allowed = fn ? SINCE_OPS : opsFor(field.kind);
    if (node.kind === "compare" && !allowed.ops.includes(node.op)) {
      this.error(operatorProblem(field, node.op, fn), node.span);
      return void 0;
    }
    if (node.kind === "in" && !allowed.allowsIn) {
      this.error(operatorProblem(field, "IN", fn), node.span);
      return void 0;
    }
    const operand = fn ? { ...field, key: `${fn}(${field.key})`, kind: "number" } : field;
    const literals = node.kind === "compare" ? [node.value] : node.values;
    const values = literals.map((literal) => this.value(literal, operand));
    if (values.some((value) => value === void 0)) return void 0;
    const compiled = values;
    return node.kind === "compare" ? { kind: "compare", op: node.op, value: compiled[0] } : { kind: "in", values: compiled };
  }
  value(literal, operand) {
    switch (operand.kind) {
      case "boolean":
        if (literal.kind === "boolean") return literal.value;
        return this.error(`"${operand.key}" is yes/no, so compare it to true or false`, literal.span);
      case "number":
      case "year":
        if (literal.kind === "number") return literal.value;
        return this.error(`"${operand.key}" is a number, so compare it to a number`, literal.span);
      case "select":
        return this.optionValue(literal, operand);
      case "text":
      case "id":
        if (literal.kind !== "boolean") return literalText(literal);
        return this.error(`"${operand.key}" is text, so compare it to a word or "quoted text"`, literal.span);
      case "brand":
        return this.brandValue(literal);
      case "date":
      case "opaque":
        return this.error(`"${operand.key}" can't be compared to a value`, literal.span);
    }
  }
  optionValue(literal, field) {
    const options = field.options ?? [];
    const wanted = literal.kind === "boolean" ? void 0 : literalText(literal).toLowerCase();
    const option = options.find((o) => o.value.toLowerCase() === wanted);
    if (option) return option.value;
    const byTitle = options.find((o) => o.title.toLowerCase() === wanted);
    const hint = byTitle ? ` Did you mean ${byTitle.value} (the value behind "${byTitle.title}")?` : "";
    const shown = literal.kind === "boolean" ? `${literal.value}` : literalText(literal);
    return this.error(
      `"${shown}" isn't an option of "${field.key}".${hint} Options: ${options.map((o) => o.value).join(", ")}`,
      literal.span
    );
  }
  brandValue(literal) {
    if (literal.kind === "boolean") return this.error("Compare a brand to a brand name", literal.span);
    const name = literalText(literal);
    const match = matchBrandName(this.catalog.brands, name, this.catalog.sharedNameplates);
    switch (match.kind) {
      case "catalog":
        return { brandId: match.id };
      case "uncatalogued":
        this.warnings.push({
          message: `"${name}" isn't in the brand catalog, so it only matches that exact name. Add it (with its aliases) to match other spellings.`,
          ...literal.span
        });
        return { brandKey: match.key };
      case "shared":
        return this.error(
          `"${name}" is a nameplate two companies share. Name the one you mean${match.qualified.length ? `: ${match.qualified.map((q) => `"${q}"`).join(" or ")}` : ""}`,
          literal.span
        );
      case "duplicate":
        return this.error(
          `Several catalog brands are called "${name}" (${match.names.join(", ")}). Use a more specific name`,
          literal.span
        );
      case "none":
        return this.error(`"${name}" isn't a brand name`, literal.span);
    }
  }
  infoField(operand) {
    const infoType = this.infoTypes.get(operand.field);
    if (!infoType) {
      this.error(
        `Unknown info type "${operand.field}"${didYouMean(operand.field, [...this.infoTypes.keys()])}`,
        operand.fieldSpan
      );
      return void 0;
    }
    if (!this.knownSpaceIds.has(infoType.spaceId)) {
      this.error(
        `"${infoType.id}" belongs to the "${infoType.spaceId}" space, which isn't published (or no longer exists)`,
        operand.fieldSpan
      );
      return void 0;
    }
    this.infoTypeIds.add(infoType.id);
    this.spaceIds.add(infoType.spaceId);
    return {
      source: "info",
      key: infoType.id,
      label: infoType.title,
      kind: fieldKindOf(infoType),
      dataType: infoType.dataType,
      options: infoType.options
    };
  }
  unitField(type, operand) {
    const core = HQL_CORE_FIELDS.find((field) => field.key === operand.field);
    if (core) return { ...core, source: "unit" };
    const coreKeys = HQL_CORE_FIELDS.map((field) => field.key);
    if (!type) {
      this.error(
        `"equipment" matches every type, so it can only filter on ${coreKeys.join(", ")}`,
        operand.fieldSpan
      );
      return void 0;
    }
    const input = type.inputFields?.find((field) => field.dataKey === operand.field);
    if (input) {
      return {
        source: "unit",
        key: input.dataKey,
        label: input.label,
        kind: fieldKindOf(input),
        dataType: input.dataType,
        options: input.options
      };
    }
    const keys = [...(type.inputFields ?? []).map((field) => field.dataKey), ...coreKeys];
    this.error(
      `"${type.id}" has no field "${operand.field}"${didYouMean(operand.field, keys)}. Fields: ${keys.join(", ")}`,
      operand.fieldSpan
    );
    return void 0;
  }
  invalid(message, span) {
    this.error(message, span);
    return INVALID_NODE;
  }
  error(message, span) {
    this.errors.push({ message, start: span.start, end: span.end });
    return void 0;
  }
};
function literalText(literal) {
  return literal.kind === "number" ? literal.raw : literal.value;
}
function operatorProblem(field, op, fn) {
  if (!fn && field.kind === "date") {
    return `"${field.key}" is a date. Compare it through years-since(), months-since() or days-since(), or use IS UNKNOWN`;
  }
  if (!fn && field.kind === "opaque") return `"${field.key}" only supports IS UNKNOWN`;
  const allowed = fn ? SINCE_OPS : opsFor(field.kind);
  const choices = [...allowed.ops, ...allowed.allowsIn ? ["IN"] : [], "IS UNKNOWN"];
  const subject = fn ? `${fn}(${field.key})` : `"${field.key}" (${describeKind(field.kind)})`;
  return `${subject} can't use ${op}. Use ${choices.join(", ")}`;
}
function didYouMean(name, candidates) {
  const lower = name.toLowerCase();
  let best;
  for (const candidate of candidates) {
    const distance = candidate.toLowerCase() === lower ? 0 : editDistance(lower, candidate.toLowerCase());
    if (distance <= 2 && (!best || distance < best.distance)) best = { candidate, distance };
  }
  return best ? ` (did you mean "${best.candidate}"?)` : "";
}
function editDistance(a, b) {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      current.push(Math.min(previous[j] + 1, current[j - 1] + 1, substitution));
    }
    previous = current;
  }
  return previous[b.length];
}

// src/evaluate.ts
import { computeTimeSinceDay, todayInTimeZone } from "@rock/util";
function evaluateHql(compiled, doc, now) {
  const completed = new Set(doc.completedSpaceIds);
  const missingSpaceIds = compiled.requiredSpaceIds.filter((spaceId) => !completed.has(spaceId));
  if (missingSpaceIds.length > 0) return { status: "pending", covers: [], missingSpaceIds };
  const covers = coveredEquipmentOrUndefined(compiled, doc, now);
  return covers ? { status: "applies", covers, missingSpaceIds: [] } : { status: "does-not-apply", covers: [], missingSpaceIds: [] };
}
function coveredEquipment(compiled, doc, now) {
  return coveredEquipmentOrUndefined(compiled, doc, now) ?? [];
}
function queryHolds(compiled, doc, now) {
  return coveredEquipmentOrUndefined(compiled, doc, now) !== void 0;
}
function coveredEquipmentOrUndefined(compiled, doc, now) {
  const result = new Evaluator(doc, now).node(compiled.root);
  if (result.value !== true) return void 0;
  return doc.equipment.filter((unit) => result.covers.has(unit.id)).map((unit) => unit.id);
}
var Evaluator = class {
  doc;
  /** The home's own calendar day at `now`, so every host counts `*-since()` to the same day. */
  today;
  constructor(doc, now) {
    this.doc = doc;
    this.today = todayInTimeZone(now, doc.timeZone);
  }
  node(node) {
    switch (node.kind) {
      case "and": {
        const results = node.operands.map((operand) => this.node(operand));
        const value = and(results.map((result) => result.value));
        return { value, covers: value === true ? union(results) : /* @__PURE__ */ new Set() };
      }
      case "or": {
        const results = node.operands.map((operand) => this.node(operand));
        const value = or(results.map((result) => result.value));
        return {
          value,
          covers: value === true ? union(results.filter((result) => result.value === true)) : /* @__PURE__ */ new Set()
        };
      }
      case "not":
        return { value: not(this.node(node.operand).value), covers: /* @__PURE__ */ new Set() };
      case "home":
        return { value: node.filter ? this.filter(node.filter, void 0) : true, covers: /* @__PURE__ */ new Set() };
      case "units": {
        const units = this.doc.equipment.filter((unit) => !node.typeId || unit.typeId === node.typeId);
        const matched = units.filter((unit) => !node.filter || this.filter(node.filter, unit) === true);
        return { value: matched.length > 0, covers: new Set(matched.map((unit) => unit.id)) };
      }
    }
  }
  filter(node, unit) {
    switch (node.kind) {
      case "and":
        return and(node.operands.map((operand) => this.filter(operand, unit)));
      case "or":
        return or(node.operands.map((operand) => this.filter(operand, unit)));
      case "not":
        return not(this.filter(node.operand, unit));
      case "test":
        return this.test(node, unit);
    }
  }
  test(test, unit) {
    const value = this.read(test.read, unit);
    return check(test.check, value, test.read);
  }
  /** The operand's value, or undefined when it's unknown. */
  read(read, unit) {
    const raw = readRaw(read, unit, this.doc);
    if (!read.fn) return raw;
    return computeTimeSinceDay(raw, SINCE_UNITS[read.fn], this.today) ?? void 0;
  }
};
function readRaw(read, unit, doc) {
  if (read.source === "info") return doc.info[read.key];
  if (!unit) return void 0;
  if (read.key === "id") return unit.id;
  if (read.key === "brand")
    return unit.brandId || unit.brandKey ? { brandId: unit.brandId, brandKey: unit.brandKey } : void 0;
  return unit.fields[read.key];
}
function check(check2, value, read) {
  if (check2.kind === "unknown") return value === void 0;
  if (value === void 0) return null;
  if (check2.kind === "in") return or(check2.values.map((expected) => compare("==", value, expected, read)));
  return compare(check2.op, value, check2.value, read);
}
function compare(op, actual, expected, read) {
  if (typeof expected === "object") return brandEquals(op, actual, expected);
  if (typeof expected === "number") return typeof actual === "number" ? compareNumbers(op, actual, expected) : null;
  if (typeof expected === "boolean") {
    if (typeof actual !== "boolean") return null;
    return op === "==" ? actual === expected : actual !== expected;
  }
  const fold = (text) => read.kind === "id" ? text : text.trim().toLowerCase();
  const values = Array.isArray(actual) ? actual : typeof actual === "string" ? [actual] : void 0;
  if (!values) return null;
  const contains = values.some((value) => fold(value) === fold(expected));
  return op === "==" ? contains : !contains;
}
function brandEquals(op, actual, expected) {
  if (typeof actual !== "object" || Array.isArray(actual)) return null;
  const same = "brandId" in expected ? actual.brandId === expected.brandId : !actual.brandId && actual.brandKey === expected.brandKey;
  const knownDifferent = !same && "brandId" in expected && !!actual.brandId;
  const equal = same ? true : knownDifferent ? false : null;
  return op === "==" ? equal : not(equal);
}
function compareNumbers(op, actual, expected) {
  switch (op) {
    case "==":
      return actual === expected;
    case "!=":
      return actual !== expected;
    case ">":
      return actual > expected;
    case ">=":
      return actual >= expected;
    case "<":
      return actual < expected;
    case "<=":
      return actual <= expected;
  }
}
function and(values) {
  if (values.includes(false)) return false;
  return values.includes(null) ? null : true;
}
function or(values) {
  if (values.includes(true)) return true;
  return values.includes(null) ? null : false;
}
function not(value) {
  return value === null ? null : !value;
}
function union(results) {
  const covers = /* @__PURE__ */ new Set();
  for (const result of results) for (const id of result.covers) covers.add(id);
  return covers;
}

// src/coverage.ts
function coverageOf(recorded, query, doc, now) {
  const ids = recordedEquipmentIds(recorded);
  if (ids) {
    const recordedIds = new Set(ids);
    return doc.equipment.filter((unit) => recordedIds.has(unit.id)).map((unit) => unit.id);
  }
  return query ? coveredEquipment(query, doc, now) : void 0;
}
function recordedEquipmentIds(recorded) {
  if (recorded.coveredEquipmentIds) return recorded.coveredEquipmentIds;
  return recorded.equipmentId ? [recorded.equipmentId] : void 0;
}

// src/facts.ts
function hqlFacts(compiled, doc) {
  const typeIds = new Set(compiled.equipmentTypeIds);
  return {
    info: Object.fromEntries(compiled.infoTypeIds.map((id) => [id, doc.info[id] ?? null])),
    equipment: doc.equipment.filter((unit) => compiled.readsAllEquipment || typeIds.has(unit.typeId))
  };
}
function hqlFieldsRead(compiled) {
  const reads = /* @__PURE__ */ new Map();
  const visitFilter2 = (filter, typeId) => {
    if (filter.kind === "test") {
      const { source, key } = filter.read;
      const read = source === "info" ? { source, key } : { source, key, ...typeId ? { typeId } : {} };
      reads.set(JSON.stringify(read), read);
    } else if (filter.kind === "not") {
      visitFilter2(filter.operand, typeId);
    } else {
      for (const operand of filter.operands) visitFilter2(operand, typeId);
    }
  };
  const visit = (node) => {
    if (node.kind === "and" || node.kind === "or") for (const operand of node.operands) visit(operand);
    else if (node.kind === "not") visit(node.operand);
    else if (node.filter) visitFilter2(node.filter, node.kind === "units" ? node.typeId : void 0);
  };
  visit(compiled.root);
  return [...reads.values()];
}

// src/highlight.ts
var SINCE_FNS = new Set(HQL_SINCE_FNS);
function highlightHql(text) {
  const lexed = lex(text);
  if (!lexed.ok) return text ? [{ text, role: "plain" }] : [];
  const parsed = parseHql(text);
  const roles = parsed.ok ? wordRoles(parsed.ast) : /* @__PURE__ */ new Map();
  const highlights = [];
  let at = 0;
  for (const token of lexed.tokens) {
    if (token.type === "eof") break;
    if (token.start > at) highlights.push({ text: text.slice(at, token.start), role: "plain" });
    highlights.push({ text: text.slice(token.start, token.end), role: roleOf(token, roles) });
    at = token.end;
  }
  if (at < text.length) highlights.push({ text: text.slice(at), role: "plain" });
  return highlights;
}
function roleOf(token, roles) {
  switch (token.type) {
    case "number":
      return "number";
    case "string":
      return "string";
    case "op":
      return "operator";
    case "punct":
      return "punctuation";
    case "word":
      return roles.get(token.start) ?? wordRole(token.text);
  }
}
function wordRole(word) {
  if (isKeyword(word)) return word.toLowerCase() === "true" || word.toLowerCase() === "false" ? "value" : "keyword";
  return SINCE_FNS.has(word.toLowerCase()) ? "function" : "plain";
}
function wordRoles(ast) {
  const roles = /* @__PURE__ */ new Map();
  const mark = (span, role) => roles.set(span.start, role);
  const visitFilter2 = (filter) => {
    if (filter.kind === "and" || filter.kind === "or") filter.operands.forEach(visitFilter2);
    else if (filter.kind === "not") visitFilter2(filter.operand);
    else {
      mark(filter.operand.fieldSpan, "field");
      const values = filter.kind === "in" ? filter.values : filter.kind === "compare" ? [filter.value] : [];
      for (const value of values) mark(value.span, "value");
    }
  };
  const visit = (node) => {
    if (node.kind === "and" || node.kind === "or") node.operands.forEach(visit);
    else if (node.kind === "not") visit(node.operand);
    else {
      mark(node.subjectSpan, "subject");
      if (node.filter) visitFilter2(node.filter);
    }
  };
  visit(ast);
  return roles;
}

// src/homeCoverage.ts
import { finishedSpaceIds, isCustomId } from "@rock/types";

// src/homeDocument.ts
import { knownTimeZone, parseFlexibleDate, unitBrandName } from "@rock/util";
function buildHomeDocument(home, catalog) {
  const types = equipmentTypesById(catalog);
  return {
    completedSpaceIds: [...new Set(home.completedSpaceIds)].sort(),
    timeZone: knownTimeZone(home.timeZone),
    info: projectInfo(home.systemInfo, catalog),
    equipment: home.equipment.filter((equipment) => equipment.active).map((equipment) => projectUnit(equipment, types.get(equipment.typeId), catalog)).sort((a, b) => compareText(a.typeId, b.typeId) || compareText(a.id, b.id))
  };
}
function serializeHomeDocument(doc) {
  return JSON.stringify(doc, sortedKeys, 2);
}
function unitCoverageInputs(equipment) {
  return JSON.stringify(
    {
      id: equipment.id,
      active: equipment.active,
      typeId: equipment.typeId,
      spaceId: equipment.spaceId,
      brand: equipment.details?.brand,
      fields: equipment.fields
    },
    sortedKeys
  );
}
function infoCoverageInputs(info) {
  return JSON.stringify(
    {
      id: info.id,
      active: info.active,
      typeId: info.typeId,
      inputValue: info.inputValue,
      createdAt: info.createdAt
    },
    sortedKeys
  );
}
function projectUnit(equipment, type, catalog) {
  const stored = equipment.fields ?? {};
  const fields = {};
  const brand = unitBrandName(equipment);
  if (brand) fields.brand = brand;
  for (const core of HQL_CORE_FIELDS) {
    if (core.dataType === "id" || core.dataType === "brand") continue;
    setValue(fields, core.key, normalizeValue(stored[core.key], { dataType: core.dataType }));
  }
  for (const input of type?.inputFields ?? []) {
    if (HQL_CORE_FIELDS.some((core) => core.key === input.dataKey)) continue;
    setValue(fields, input.dataKey, normalizeValue(stored[input.dataKey], input));
  }
  return {
    id: equipment.id,
    typeId: equipment.typeId,
    spaceId: equipment.spaceId,
    ...resolveUnitBrand(
      catalog.brands,
      typeof fields.brand === "string" ? fields.brand : void 0,
      equipment.spaceId,
      catalog.sharedNameplates
    ),
    fields
  };
}
function projectInfo(systemInfo, catalog) {
  const infoTypes = new Map(catalog.infoTypes.map((type) => [type.id, type]));
  const info = {};
  const active = systemInfo.filter((record) => record.active).sort((a, b) => compareText(a.createdAt ?? "", b.createdAt ?? "") || compareText(a.id, b.id));
  for (const record of active) {
    const type = infoTypes.get(record.typeId);
    if (type) setValue(info, record.typeId, normalizeValue(record.inputValue, type));
  }
  return info;
}
function normalizeValue(raw, field) {
  switch (fieldKindOf(field)) {
    case "boolean":
      return readBoolean(raw);
    case "select":
      return readOptions(raw, field.options ?? []);
    case "text":
      return readStrings(raw);
    case "number":
      return readNumber(raw);
    case "year": {
      const year = readNumber(raw);
      return year !== void 0 && year >= 1e3 && year <= 9999 ? year : void 0;
    }
    case "date":
      return readDate(raw);
    case "brand":
    case "id":
      return readString2(raw);
    case "opaque":
      return readOpaque(raw);
  }
}
function readBoolean(raw) {
  if (typeof raw === "boolean") return raw;
  const text = typeof raw === "string" ? raw.trim().toLowerCase() : void 0;
  return text === "true" ? true : text === "false" ? false : void 0;
}
function readString2(raw) {
  if (typeof raw === "number" && Number.isFinite(raw)) return `${raw}`;
  if (typeof raw !== "string") return void 0;
  const text = raw.trim();
  return text || void 0;
}
function readStrings(raw) {
  if (!Array.isArray(raw)) return readString2(raw);
  const values = raw.map(readString2).filter((value) => value !== void 0);
  return values.length > 0 ? values : void 0;
}
function readOptions(raw, options) {
  const optionValue = (text) => options.find((option) => option.value.toLowerCase() === text.toLowerCase())?.value;
  const values = readStrings(raw);
  if (values === void 0) return void 0;
  if (!Array.isArray(values)) return optionValue(values);
  const known = values.map(optionValue).filter((value) => value !== void 0);
  return known.length > 0 ? known : void 0;
}
function readNumber(raw) {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : void 0;
  const text = typeof raw === "string" ? raw.trim() : "";
  return HQL_NUMBER.test(text) ? Number(text) : void 0;
}
var CALENDAR_DATE = /^\d{4}(-\d{2}(-\d{2})?)?$/;
var TIMESTAMP = /^\d{4}-\d{2}-\d{2}T/;
function readDate(raw) {
  const text = readString2(raw);
  const date = text && TIMESTAMP.test(text) ? text.slice(0, 10) : text;
  return date && CALENDAR_DATE.test(date) && parseFlexibleDate(date) ? date : void 0;
}
function readOpaque(raw) {
  if (Array.isArray(raw)) return readStrings(raw);
  if (raw && typeof raw === "object") {
    const url = raw.url;
    if (typeof url === "string" && url) return url;
    return Object.keys(raw).length > 0 ? JSON.stringify(raw, sortedKeys) : void 0;
  }
  return typeof raw === "boolean" ? void 0 : readString2(raw);
}
function setValue(target, key, value) {
  if (value !== void 0) target[key] = value;
}
function compareText(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}
function sortedKeys(_key, value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return Object.fromEntries(Object.entries(value).sort(([a], [b]) => compareText(a, b)));
}

// src/homeCoverage.ts
function coverageQueries(catalog, considerationTypes, customTaskTypes) {
  const queries = /* @__PURE__ */ new Map();
  const add = (key, text, spaceId) => {
    const compiled = text?.trim() ? checkHql(text, catalog, { spaceId }).compiled : void 0;
    if (compiled) queries.set(key, compiled);
  };
  for (const type of considerationTypes) add(type.id, type.appliesTo, type.spaceId);
  for (const type of customTaskTypes) add(type.id, type.appliesTo);
  return queries;
}
function homeCoverage(sources, now, equipment = sources.equipment) {
  const { home, systemInfo, catalog, queries } = sources;
  const doc = buildHomeDocument(
    { equipment, systemInfo, completedSpaceIds: finishedSpaceIds(home), timeZone: home.geo?.timezone },
    catalog
  );
  return coverageIn(doc, queries, now);
}
function coverageIn(doc, queries, now) {
  return {
    doc,
    ofSubscription: (subscription) => coverageOf(subscription, queries.get(queryKey(subscription)), doc, now),
    ofConsideration: (consideration) => coverageOf(consideration, queries.get(consideration.typeId), doc, now),
    queryHolds: (subscription) => holds(queryKey(subscription), subscription),
    considerationQueryHolds: (consideration) => holds(consideration.typeId, consideration)
  };
  function holds(key, item) {
    const query = queries.get(key);
    if (!query || recordedEquipmentIds(item)) return void 0;
    return queryHolds(query, doc, now);
  }
}
function appliesToHome(coverage, subscription) {
  return !!coverage.ofSubscription(subscription)?.length || coverage.queryHolds(subscription) === true;
}
function considerationAppliesToHome(coverage, consideration) {
  return !!coverage.ofConsideration(consideration)?.length || coverage.considerationQueryHolds(consideration) === true;
}
function shouldWithdraw(suggestion, before, after) {
  if (suggestion.status !== "pending" || suggestion.snoozedAt) {
    return false;
  }
  return considerationAppliesToHome(before, suggestion) && !considerationAppliesToHome(after, suggestion);
}
function taskCoverage(task, reminder, coverage) {
  const recorded = recordedEquipmentIds(task);
  if (task.status === "completed" && recorded) return recorded;
  return (reminder && coverage.ofSubscription(reminder)) ?? recorded;
}
function queryKey(subscription) {
  return isCustomId(subscription.taskTypeId) ? subscription.taskTypeId : subscription.considerationTypeId ?? "";
}

// src/languageReference.ts
var HQL_REFERENCE = `# HQL (Home Query Language)

A query decides whether a consideration applies to a home, and which of the home's equipment it covers.

## Terms

A query is made of terms, combined with AND, OR, NOT and parentheses.

| Term | True when | Covers |
|---|---|---|
| \`mini-split\` | the home has at least one mini-split | every mini-split |
| \`furnace-unit[fuelSource == natural-gas]\` | at least one furnace burns natural gas | those furnaces |
| \`equipment[brand == "Zinsco"]\` | any unit, of any type, is a Zinsco | those units |
| \`home\` | always (every home) | nothing |
| \`home[gfci == false]\` | the home's answer for the \`gfci\` info type is No | nothing |

- A subject is an equipment type id (\`furnace-unit\`), \`equipment\` (a unit of any type) or \`home\`.
- A filter in [...] tests fields. An equipment type's filter can use the type's input fields (by data key)
  and the core fields \`brand\`, \`modelNumber\`, \`serialNumber\` and \`id\`. \`equipment[...]\` can use only
  the core fields. A \`home[...]\` filter tests info types, by info type id.
- Only active equipment counts.

## Comparisons

\`field == value\`, \`!=\`, \`>\`, \`>=\`, \`<\`, \`<=\`, \`field IN (a, b, c)\` and \`field IS UNKNOWN\`.

| Field data type | Allowed |
|---|---|
| boolean | \`==\`, \`!=\` against true or false |
| select | \`==\`, \`!=\`, IN, against option values (not titles) |
| number, quantity, year | \`==\`, \`!=\`, \`<\`, \`<=\`, \`>\`, \`>=\`, IN |
| month-year, date | only through years-since(), months-since() or days-since() |
| text, model-number, serial-number | \`==\`, \`!=\`, IN (ignores case and surrounding spaces) |
| the core \`brand\` field | \`==\`, \`!=\`, IN, against brand names (other brand fields compare as text) |
| id | \`==\`, \`!=\`, IN (exact) |
| dimensions, address, image, room-id | IS UNKNOWN only |

Every field supports IS UNKNOWN.

\`years-since(field)\`, \`months-since(field)\` and \`days-since(field)\` count whole years, months or days from a
year or date field until today, e.g. \`years-since(last-electrical-inspection) >= 10\`. A bare year counts
from January 1 of that year.

Values are numbers (\`10\`), true or false, bare words (\`natural-gas\`) or "quoted text" (\`"Federal Pacific"\`).
Keywords are case-insensitive. Against a multi-select field, \`==\` means "contains", \`!=\` means "doesn't
contain", and IN means "contains any of".

## Unknown values

A field that is missing, empty or unreadable is unknown. Inside a filter, comparing an unknown value gives
unknown, not false. NOT unknown is still unknown. AND is false if any side is false, OR is true if any side
is true. Only IS UNKNOWN matches an unknown value.

So \`furnace-unit[fuelSource != electric]\` does not match a furnace whose fuel is unknown. To include it,
say so: \`furnace-unit[fuelSource != electric OR fuelSource IS UNKNOWN]\`.

A \`home[...]\` term keeps its filter's answer, unknown included, and a query that is unknown overall does
not apply. An equipment term is never unknown: it is true when at least one unit definitely matches, and
false otherwise. So \`NOT furnace-unit[fuelSource == electric]\` applies to a home whose only furnace has an
unknown fuel. To leave such homes out, write \`NOT furnace-unit[fuelSource == electric OR fuelSource IS UNKNOWN]\`.

## Applies and covers

- An equipment term covers exactly the units whose filter is definitely true.
- A \`home\` term covers no units.
- A AND B applies when both apply, and covers the units of both. A OR B applies when either applies, and
  covers the units of the sides that apply. NOT A covers nothing.
- A query with no equipment term outside a NOT covers no equipment: it is about the whole home.
- A query waits until the home has completed its consideration's space and the spaces of the equipment
  types and info types it reads. \`equipment[...]\` adds no space to wait for.

## Brands

Brand names match through the brand catalog, aliases included, so "Square D" also matches a unit entered
as "Square D by Schneider Electric". A name the catalog lacks matches only its own spelling (ignoring
case, spaces and punctuation). Two brands are known to differ only when both are in the catalog, so \`!=\`
against a spelling the catalog lacks is unknown. A nameplate two companies share (a shared nameplate, such
as Maytag) must be qualified, e.g. "Maytag Appliances".

## Rules

- A query is never empty. Write \`home\` to apply to every home.
- Use ids, data keys and option values exactly as the catalog defines them, not titles.
- Write the simplest query that says what the text says. When the condition is about equipment, use an
  equipment term (not a \`home\` term), so the consideration covers those units.

## Examples

| When To Apply | Query |
|---|---|
| homes that have mini-split units | \`mini-split\` |
| any home with a furnace unit or air handler | \`furnace-unit OR air-handler\` |
| furnaces burning natural gas, propane or oil; still apply if the fuel is unknown | \`furnace-unit[fuelSource IN (natural-gas, propane, oil) OR fuelSource IS UNKNOWN]\` |
| electric furnaces; not if the fuel is unknown | \`furnace-unit[fuelSource == electric]\` |
| a main panel made by Federal Pacific or Zinsco | \`main-panel[brand IN ("Federal Pacific", "Zinsco")]\` |
| homes without GFCI outlets | \`home[gfci == false]\` |
| no electrical inspection in the last 10 years, or unknown | \`home[years-since(last-electrical-inspection) >= 10 OR last-electrical-inspection IS UNKNOWN]\` |
| all homes | \`home\` |
`;

// src/references.ts
function hqlReferences(ast) {
  const refs = { equipmentTypeIds: /* @__PURE__ */ new Set(), infoTypeIds: /* @__PURE__ */ new Set(), brands: /* @__PURE__ */ new Set() };
  let coversEquipment = false;
  const visit = (node, underNot) => {
    switch (node.kind) {
      case "and":
      case "or":
        for (const operand of node.operands) visit(operand, underNot);
        return;
      case "not":
        visit(node.operand, true);
        return;
      case "term": {
        const isHome = node.subject === "home";
        if (!isHome && !underNot) coversEquipment = true;
        if (!isHome && node.subject !== "equipment") refs.equipmentTypeIds.add(node.subject);
        if (node.filter) visitFilter(node.filter, isHome, refs);
        return;
      }
    }
  };
  visit(ast, false);
  return {
    equipmentTypeIds: [...refs.equipmentTypeIds].sort(),
    infoTypeIds: [...refs.infoTypeIds].sort(),
    brands: [...refs.brands].sort(),
    coversEquipment
  };
}
function visitFilter(node, isHome, refs) {
  switch (node.kind) {
    case "and":
    case "or":
      for (const operand of node.operands) visitFilter(operand, isHome, refs);
      return;
    case "not":
      visitFilter(node.operand, isHome, refs);
      return;
    default:
      if (isHome) {
        refs.infoTypeIds.add(node.operand.field);
      } else if (node.operand.field === "brand" && node.kind !== "is-unknown") {
        const literals = node.kind === "compare" ? [node.value] : node.values;
        for (const literal of literals)
          refs.brands.add(literal.kind === "number" ? literal.raw : `${literal.value}`);
      }
  }
}
export {
  HQL_CORE_FIELDS,
  HQL_OPS,
  HQL_REFERENCE,
  HQL_SINCE_FNS,
  appliesToHome,
  buildHomeDocument,
  canonicalHql,
  checkHql,
  compileHql,
  considerationAppliesToHome,
  coverageIn,
  coverageOf,
  coverageQueries,
  coveredEquipment,
  equipmentIdQuery,
  equipmentTypesById,
  evaluateHql,
  highlightHql,
  homeCoverage,
  hqlFacts,
  hqlFieldsRead,
  hqlReferences,
  infoCoverageInputs,
  parseHql,
  printHql,
  queryHolds,
  recordedEquipmentIds,
  sameHql,
  serializeHomeDocument,
  shouldWithdraw,
  taskCoverage,
  unitCoverageInputs
};
//# sourceMappingURL=index.js.map